import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTo";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import loadShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadShipTo";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { OrderConfirmationPageContext } from "@insite/content-library/Pages/OrderConfirmationPage";
import OrderConfirmationBillingInformation, {
    OrderConfirmationBillingInformationStyles,
} from "@insite/content-library/Widgets/OrderConfirmation/OrderConfirmationBillingInformation";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import OrderConfirmationShippingInformation, {
    OrderConfirmationShippingInformationStyles,
} from "@MasterBluePrint/Overrides/Widgets/OrderConfirmation/OrderConfirmationShippingInformation";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.orderConfirmation.cartId).value;
    return {
        cart,
        billTo: getBillToState(state, cart?.billToId).value,
        shipTo: getShipToState(state, cart?.shipToId).value,
        pickUpWarehouse: state.context.session.pickUpWarehouse,
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        language: state.context.session.language,
    };
};

const mapDispatchToProps = {
    loadBillTo,
    loadShipTo,
};

export interface OrderConfirmationOrderInformationStyles {
    OrderInformationGridContainer?: GridContainerProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeading?: TypographyProps;
    orderNumberText?: TypographyPresentationProps;
    shippingInformationGridItem?: GridItemProps;
    vatNumberGridItem?: GridItemProps;
    vatNumberTitleText?: TypographyPresentationProps;
    vatNumberText?: TypographyPresentationProps;
    orderConfirmationShippingInformation?: OrderConfirmationShippingInformationStyles;
    billingInformationGridItem?: GridItemProps;
    orderConfirmationBillingInformation?: OrderConfirmationBillingInformationStyles;
    notesGridItem?: GridItemProps;
    notesTitle: TypographyProps;
    notesDescription?: TypographyPresentationProps;
    orderDetailsGridContainer?: GridContainerProps;
    orderInformationGridItems?: GridItemProps;
    informationGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    orderDateGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    salespersonGridItem?: GridItemProps;
    dueDateGridItem?: GridItemProps;
    billingAddressGridItem?: GridItemProps;
    shippingAddressGridItem?: GridItemProps;
    orderNotesGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
}

export const orderInformationStyles: OrderConfirmationOrderInformationStyles = {
    OrderInformationGridContainer: {
        gap: 10,
        css: css`
           > div {
                width: calc(100% + 10px) !important;
                margin-top: 0;
                padding-top: .9375rem;
           } 
        `,
    },
    shippingInformationGridItem: {
        width: 12,
    },
    vatNumberGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    vatNumberTitleText: {
        variant: "h6",
        css: css`
            margin-bottom: .31250625rem;
        `,
    },
    billingInformationGridItem: {
        width: 12,
    },
    notesGridItem: {
        width: 12,
        css: css`
            padding: .625rem;
            border-radius: 0 0 2px .125rem;
            background-color: rgba(182,182,182,0.1);
            margin-bottom: .625rem !important;
        `,
    },
    notesTitle: {
        variant: "h6",
        as: "h2",
        css: css`
            margin-bottom: 0;
            margin-right: .31250625rem;
            min-width: 6.25rem;
            letter-spacing: .031rem;

            @media print {
                display: inline-block;
            }
        `,
    },
    notesDescription: {
        css: css`
            color: #333F48;
            font-family: Lato;
            font-size: 1rem;
            letter-spacing: .031rem;
            line-height: 1.5rem;

            @media print {
                display: inline-block;
            }
        `,
    },
    headingAndTextStyles: {
        heading: {
            variant: "h6",
            as: "h3",
            css: css`
                color: #333F48;
                display: flex;
                margin-bottom: .31250625rem;
                margin-right: .31250625rem;
                line-height: 1.25rem;
                font-family: Lato;
                font-size: 1rem;
                letter-spacing: .031rem;
                &:after {
                    content: ":";
                }
            `,
        },
        text: {
            as: "span",
            css: css`
                color: #333F48;
                line-height: 1.25rem;
                font-family: Lato;
                font-size: 1rem;
                letter-spacing: .031rem;
                display: flex;
                margin-bottom: .31250625rem;
                width : auto;
            `,
        }
    },
    orderInformationGridItems: {
        css: css`
            text-align: center;
            flex-direction: row;
            @media print {
                padding: .31250625rem !important;
                display: flex;
                > div {
                    display: flex;
                }
            }
        `,
    },
    statusGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            padding: 0 .9375rem;

            > div {
                flex-direction: row;
                justify-content: right;
            }

            @media print {
                display: flex;
                > div {
                    display: flex;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [   
                        css`
                            > div {
                                flex-direction: row;
                                justify-content: center;
                            }
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            };
        `,
    },
    orderDateGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            > div {
                flex-direction: row;
                justify-content: left;
            }
            @media print {
                display: flex;
                > div {
                    display: flex;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [   
                        css`
                            > div {
                                flex-direction: row;
                                justify-content: center;
                            }
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            };

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [   
                        null,
                        null,
                        css`
                            > div {
                                padding-left: .31250625rem;
                            }
                        `,
                        null,
                        null,
                    ],
                    "min",
                )
            };
        `,
    },
    poNumberGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            > div {
                flex-direction: row;
                justify-content: center;
            }
            @media print {
                display: flex;
                > div {
                    display: flex;
                }
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [   
                        css`
                            > div {
                                flex-direction: row;
                                justify-content: center;
                            }
                        `,
                        null,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )
            };
        `,
    },
    salespersonGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            > div {
                flex-direction: row;
                justify-content: center;
            }
        `,
    },
    dueDateGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            > div {
                flex-direction: row;
            }
        `,
    },
    billingAddressGridItem: {
        width: 6,
    },
    shippingAddressGridItem: {
        width: 6,
    },
    orderNotesGridItem: {
        width: 12,
    },
    orderDetailsGridContainer: {
        css: css`
            padding-left: .31250625rem;
            padding-bottom: 1.3125rem;
            border-bottom: 1px solid #C2D1D9;
            margin-bottom: .9375rem;
            margin-top: 0;
            > div {
                width: calc(100% + 15px);
                > div {
                    padding-top: 0;
                    padding-bottom: 0;
                }
            }
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const styles = orderInformationStyles;

const OrderConfirmationOrderInformation: FC<Props> = props => {
   
    const language = props.language;
    if (!props.cart) {
        return null;
    }

    if (!props.billTo && props.cart.billToId) {
        props.loadBillTo({ billToId: props.cart.billToId });
    }

    if (!props.shipTo && props.cart.billToId && props.cart.shipToId) {
        props.loadShipTo({ billToId: props.cart.billToId, shipToId: props.cart.shipToId });
    }

    if (!props.billTo || !props.shipTo) {
        return null;
    }
    if (!props.cart.paymentMethod) {
        return null;
    }

    const textForOrdenotes =  props.cart.notes.toString().replace("\\n"," ");
    let PaymentMethod;
    if (props.cart.paymentMethod.cardType == null) {
        PaymentMethod = props.cart.paymentMethod.description;
    }else {
        PaymentMethod = `${props.cart.paymentMethod.description } ${ props.cart.paymentMethod.cardType}`;
    }

    return (
        <GridContainer {...styles.OrderInformationGridContainer}>
            <GridContainer {...styles.orderDetailsGridContainer} className="order-container">
                <GridItem {...mergeToNew(styles.orderInformationGridItems, styles.orderDateGridItem)}>
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("Order Date")}
                        text={getLocalizedDateTime({
                            dateTime: new Date(props.cart.orderDate as Date),
                            language,
                        })}
                    />
                </GridItem>
                <GridItem {...mergeToNew(styles.orderInformationGridItems, styles.poNumberGridItem)}>
                    { props.cart.poNumber !== '' ? <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("PO Number")}
                        text={props.cart.poNumber}
                    /> : '' }
                </GridItem>
                <GridItem {...mergeToNew(styles.orderInformationGridItems, styles.statusGridItem)}>
                    <SmallHeadingAndText
                        extendedStyles={styles.headingAndTextStyles}
                        heading={translate("Payment Method")}
                        text={PaymentMethod}
                    />
                </GridItem>
            </GridContainer>
            {props.cart.notes && (
                <GridItem {...styles.notesGridItem}>
                    <Typography {...styles.notesTitle}>{translate("Order Notes:")}</Typography>
                    <Typography {...styles.notesDescription}>{textForOrdenotes}</Typography>
                </GridItem>
            )}
            <GridItem {...styles.shippingInformationGridItem}>
                <OrderConfirmationShippingInformation
                    cart={props.cart}
                    shipTo={props.shipTo}
                    pickUpWarehouse={props.pickUpWarehouse}
                    extendedStyles={styles.orderConfirmationShippingInformation}
                    billTo={props.billTo}
                />
            </GridItem>
            {props.enableVat && props.cart.customerVatNumber && (
                <GridItem {...styles.vatNumberGridItem}>
                    <Typography {...styles.vatNumberTitleText} as="h3">
                        {translate("VAT Number")}
                    </Typography>
                    <Typography {...styles.vatNumberText}>{props.cart.customerVatNumber}</Typography>
                </GridItem>
            )}
            
        </GridContainer>
    );
};
const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OrderConfirmationOrderInformation),
    definition: {
        displayName: "Order Information",
        allowedContexts: [OrderConfirmationPageContext],
        group: "Order Confirmation",
    },
};

export default widgetModule;